import fileDownload from 'js-file-download';
import i18n from 'i18next';
import moment from 'moment';
import { languageCodeOrFallback } from 'src/i18n/helpers';
import axios from '../../../shared/utils/axios';
import { addToast } from '../toasts/toastsActions';
import { toastTypes } from '../../../shared/components/Toast/ToastOptions';

export const FILE_FEEDBACK = 'FILE_FEEDBACK';
export const UNKNOWN_ERROR = 'UNKNOWN_ERROR';
export const WRONG_FILE_FORMAT = 'WRONG_FILE_FORMAT';
export const CLEAR_FILE_FEEDBACK = 'CLEAR_FILE_FEEDBACK';
export const FILE_LOADING_START = 'FILE_LOADING_START';
export const FILE_LOADING_END = 'FILE_LOADING_END';

const employeesEndpoint = `${process.env.REACT_APP_API_URL}employees`;
const uploadFileEndpoint = type => `${employeesEndpoint}/bulk/${type}`;
const downloadEmployeesFileEndpoint = `${employeesEndpoint}/file/excel`;
const downloadWithdrawalsFileEndpoint = `${process.env.REACT_APP_API_URL}company/withdrawals/file/excel`;

export const fileUploadLoadingStart = () => ({
  type: FILE_LOADING_START
});

export const fileUploadLoadingEnd = () => ({
  type: FILE_LOADING_END
});

export const fileFeedback = feedback => ({
  type: FILE_FEEDBACK,
  fileFeedback: feedback
});

export const clearFileFeedback = dispatch =>
  dispatch({
    type: CLEAR_FILE_FEEDBACK
  });

export const fileOperationFailed = () => ({
  type: UNKNOWN_ERROR
});

export const wrongFileFormat = () => ({
  type: WRONG_FILE_FORMAT
});

export const downloadPayflowFile = async (data, locale, key = 'employees.file.download.name') => {
  const languageFromLocale = languageCodeOrFallback(locale);

  await i18n.loadLanguages([languageFromLocale]);

  const fileName = i18n.t(key, {
    lng: languageFromLocale,
    ns: ['actions']
  });

  try {
    fileDownload(data, `${fileName}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
  } catch (error) {}
};

export const downloadEmployeesFile =
  (locale, filters = {}) =>
  async dispatch => {
    const searchTermQuery = filters.searchTerm
      ? new URLSearchParams({ searchTerm: filters.searchTerm }).toString()
      : '';

    const currentFilters = filters.currentFilters || '';
    const query = `${searchTermQuery}${currentFilters}`;
    const url = query ? `${downloadEmployeesFileEndpoint}?${query}` : downloadEmployeesFileEndpoint;

    try {
      const response = await axios.get(url, {
        responseType: 'arraybuffer'
      });

      await downloadPayflowFile(response.data, locale);
      dispatch(fileFeedback());
    } catch (error) {
      dispatch(fileFeedback(i18n.t('employees.file.download.error', { ns: ['actions'] })));
      dispatch(addToast(toastTypes.error, error));
    }
  };

export const downloadWithdrawsFile =
  (locale, filters = {}) =>
  async dispatch => {
    const filteredQuery = {};
    let url = downloadWithdrawalsFileEndpoint + '?';

    for (const [key, value] of Object.entries(filters)) {
      if (!['offset', 'page', 'currentFilters'].includes(key)) {
        if ((key === 'startDate' || key === 'endDate') && moment(value).isValid()) {
          filteredQuery[key] = moment(value).format('YYYY-MM-DD');
        }

        if (value?.length > 0) {
          filteredQuery[key] = value;
        }
      }
    }

    const filtersSanitized = new URLSearchParams(filteredQuery).toString();
    url = url + filtersSanitized;

    if (filters.currentFilters?.length) {
      url = url + filters.currentFilters;
    }

    try {
      const response = await axios.get(url, { responseType: 'arraybuffer' });

      await downloadPayflowFile(response.data, locale, 'withdrawals.file.download.name');
      dispatch(fileFeedback());
    } catch (error) {
      dispatch(addToast(toastTypes.error, error));
      console.error(error);
    }
  };

export const uploadFile = (file, type) => async dispatch => {
  const fileType = file?.type || 'text/plain; charset=us-ascii';
  const typeAndExtension = type;

  const result = responseResult =>
    i18n.t(`employees.file.upload.${responseResult}`, {
      ns: ['actions']
    });

  try {
    dispatch(fileUploadLoadingStart());

    const response = await axios.post(uploadFileEndpoint(typeAndExtension), file, {
      headers: {
        'Content-Type': fileType
      }
    });

    if (response.status === 202) {
      dispatch(
        addToast({
          ...toastTypes.success,
          defaultMessage: result(response.data.result)
        })
      );
    } else {
      dispatch(fileFeedback(response.data));
    }
  } catch (error) {
    if (error?.response?.data) {
      switch (error?.response?.data?.statusCode) {
        case 500:
          dispatch(fileOperationFailed());
          break;
        case 415:
          dispatch(wrongFileFormat());
          break;
        default:
          dispatch(fileFeedback(error.response.data));
      }
    }
    dispatch(addToast(toastTypes.error, error));
  } finally {
    dispatch(fileUploadLoadingEnd());
  }
};
